import React, { useCallback, useState } from 'react';

import styled, { css } from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';

const TableWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.small}px;
  padding-top: ${({ theme }) => theme.spacing.large}px;
  ${mobile`
    padding-top: ${({ theme }) => theme.spacing.moderate}px;
    flex-wrap: wrap;
  `}
`;

const Table = styled.table<{ striped?: boolean; compact?: boolean }>`
  width: 343px;
  table-layout: fixed;
  border-collapse: collapse;
  margin: 0 auto;

  ${mobile`
    width: 100%;
    margin: 0;
  `}
  th,
  td {
    font-size: 14px;
    font-weight: 400;
    border: 1px solid ${({ theme }) => theme.colors.black};
    padding: ${({ theme }) => theme.spacing.small}px
      ${({ theme }) => theme.spacing.tiny}px;
    text-align: center;
    ${({ theme, compact }) =>
      compact
        ? theme.paragraph.variants.paragraph5Uppercase.textStyles
        : theme.paragraph.variants.paragraph4Uppercase.textStyles}
  }

  th {
    ${({ theme }) => theme.paragraph.variants.paragraph5Uppercase.textStyles}
    background-color: ${({ theme }) => theme.colors.marble};
  }

  ${({ striped }) =>
    striped &&
    css`
      tbody tr:nth-of-type(even) {
        background-color: ${({ theme }) => theme.colors.marble};
      }
    `}
`;

const BufferRow = styled.tr`
  padding: 0;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border-left: none;
  border-right: none;
`;

const WideColumn = styled.th`
  width: 120px;
`;

const WirelessColumn = styled.th`
  width: 48px;
`;

const NarrowColumn = styled.th`
  width: 54px;
`;

const IntColumn = styled.th`
  width: 70px;
`;

const SportsColumn = styled.th`
  width: 60px;
`;
const SportsColumnDecimal = styled.th`
  width: 70px;
`;

const IntSizesColumnHeader = styled(IntColumn)`
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing.tiny}px;
  position: relative;
  ${desktop`
    justify-content: center;
    width: 120%;
    left: -10%;
  `}
`;

const PillButton = styled.button<{ isActive?: boolean }>`
  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
  min-width: auto;
  padding: 10px 20px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 20px;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.black : theme.colors.gray100};
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.white : 'inherit'};
`;

type SizeChartType = 'wired' | 'wireless' | 'sports' | 'international';

const SizeChartContent: React.FC = () => {
  const [activeSizeChartTab, setActiveSizeChartTab] =
    useState<SizeChartType>('wired');

  const handleSizeChartChange = useCallback(
    (nextActiveTab: SizeChartType) => {
      return () => {
        if (activeSizeChartTab !== nextActiveTab) {
          setActiveSizeChartTab(nextActiveTab);
        }
      };
    },
    [activeSizeChartTab, setActiveSizeChartTab]
  );

  return (
    <>
      <Pills>
        <PillButton
          onClick={handleSizeChartChange('wired')}
          type="button"
          isActive={activeSizeChartTab === 'wired'}
        >
          <FormattedMessage
            id="fit_guide.bra_type_selector_bras"
            defaultMessage="Bras"
          />
        </PillButton>
        <PillButton
          onClick={handleSizeChartChange('wireless')}
          type="button"
          isActive={activeSizeChartTab === 'wireless'}
        >
          <FormattedMessage
            id="fit_guide.bra_type_selector_bralettes"
            defaultMessage="Bralettes"
          />
        </PillButton>
        <PillButton
          onClick={handleSizeChartChange('sports')}
          type="button"
          isActive={activeSizeChartTab === 'sports'}
        >
          <FormattedMessage
            id="fit_guide.bra_type_selector_sports_lounge"
            defaultMessage="Sports & Lounge"
          />
        </PillButton>
        <PillButton
          onClick={handleSizeChartChange('international')}
          type="button"
          isActive={activeSizeChartTab === 'international'}
        >
          <FormattedMessage
            id="fit_guide.bra_type_selector_international_sizes"
            defaultMessage="International Sizes"
          />
        </PillButton>
      </Pills>
      {activeSizeChartTab === 'wired' && (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <WideColumn>
                  <FormattedMessage
                    id="fit_guide.underbust"
                    defaultMessage="Underbust Measurements"
                  />
                </WideColumn>
                <NarrowColumn>
                  <FormattedMessage
                    id="fit_guide.band_size"
                    defaultMessage="Band Size"
                  />
                </NarrowColumn>
                <WideColumn>
                  <FormattedMessage
                    id="fit_guide.overbust_measurements"
                    defaultMessage="Overbust Measurements"
                  />
                </WideColumn>
                <NarrowColumn>
                  <FormattedMessage
                    id="fit_guide.cup_size"
                    defaultMessage="Cup Size"
                  />
                </NarrowColumn>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td rowSpan={4}>24½" - 26½"</td>
                <td rowSpan={4}>30</td>
                <td>30½" - 31¼"</td>
                <td>A</td>
              </tr>
              <tr>
                <td>31½" - 32¼"</td>
                <td>B</td>
              </tr>
              <tr>
                <td>32½" - 33¼"</td>
                <td>C</td>
              </tr>
              <tr>
                <td>33½" - 34½"</td>
                <td>D</td>
              </tr>
              <tr>
                <td rowSpan={6}>26½" - 28¼"</td>
                <td rowSpan={6}>32</td>
                <td>30½" - 31¼"</td>
                <td>A</td>
              </tr>
              <tr>
                <td>31½" - 32¼"</td>
                <td>B</td>
              </tr>
              <tr>
                <td>32½" - 33¼"</td>
                <td>C</td>
              </tr>
              <tr>
                <td>33½" - 34½"</td>
                <td>D</td>
              </tr>
              <tr>
                <td>34½" - 35½"</td>
                <td>DD</td>
              </tr>
              <tr>
                <td>35½" - 36½"</td>
                <td>DDD</td>
              </tr>
            </tbody>
          </Table>
        </TableWrapper>
      )}
      {activeSizeChartTab === 'international' && (
        <TableWrapper>
          <Table striped>
            <thead>
              <tr>
                <IntColumn>
                  <FormattedMessage id="fit_guide.us" defaultMessage="US" />
                </IntColumn>
                <th>
                  <FormattedMessage id="fit_guide.uk" defaultMessage="UK" />
                </th>
                <IntColumn>
                  <FormattedMessage
                    id="fit_guide.fr_es"
                    defaultMessage="FR/ES"
                  />
                </IntColumn>
                <IntSizesColumnHeader>
                  <FormattedMessage
                    id="fit_guide.int"
                    defaultMessage="INT (DE/EU/CHINA)"
                  />
                </IntSizesColumnHeader>
                <IntColumn>
                  <FormattedMessage id="fit_guide.au" defaultMessage="AU" />
                </IntColumn>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>30A</td>
                <td>30A</td>
                <td>80A</td>
                <td>65A</td>
                <td>8A</td>
              </tr>
              <tr>
                <td>30B</td>
                <td>30B</td>
                <td>80B</td>
                <td>65B</td>
                <td>8B</td>
              </tr>
              <tr>
                <td>30C</td>
                <td>30C</td>
                <td>80C</td>
                <td>65C</td>
                <td>8C</td>
              </tr>
              <tr>
                <td>30D</td>
                <td>30D</td>
                <td>80D</td>
                <td>65D</td>
                <td>8D</td>
              </tr>

              <BufferRow />
              <tr>
                <td>32A</td>
                <td>32A</td>
                <td>85A</td>
                <td>70A</td>
                <td>10A</td>
              </tr>
              <tr>
                <td>32B</td>
                <td>32B</td>
                <td>85B</td>
                <td>70B</td>
                <td>10B</td>
              </tr>
              <tr>
                <td>32C</td>
                <td>32C</td>
                <td>85C</td>
                <td>70C</td>
                <td>10C</td>
              </tr>
              <tr>
                <td>32D</td>
                <td>32D</td>
                <td>85D</td>
                <td>70D</td>
                <td>10D</td>
              </tr>
            </tbody>
          </Table>
        </TableWrapper>
      )}
      {activeSizeChartTab === 'wireless' && (
        <TableWrapper>
          <Table striped>
            <thead>
              <tr>
                <WirelessColumn>
                  <FormattedMessage id="fit_guide.size" defaultMessage="Size" />
                </WirelessColumn>
                <WirelessColumn>
                  <FormattedMessage
                    id="fit_guide.chest"
                    defaultMessage="Chest"
                  />
                </WirelessColumn>
                <WirelessColumn>
                  <FormattedMessage
                    id="fit_guide.bra_size"
                    defaultMessage="Bra Size"
                  />
                </WirelessColumn>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>XS 0-2</td>
                <td>32" - 34"</td>
                <td>30A, 32A, 32B</td>
              </tr>
              <tr>
                <td>S 4-6</td>
                <td>34" - 36"</td>
                <td>30A, 32A, 32B</td>
              </tr>
              <tr>
                <td>M 8-10</td>
                <td>34" - 36"</td>
                <td>30A, 32A, 32B</td>
              </tr>
              <tr>
                <td>L 12-14</td>
                <td>34" - 36"</td>
                <td>30A, 32A, 32B</td>
              </tr>
              <tr>
                <td>XL 14-16</td>
                <td>34" - 36"</td>
                <td>30A, 32A, 32B</td>
              </tr>
              <tr>
                <td>1X 16-18</td>
                <td>34" - 36"</td>
                <td>30A, 32A, 32B</td>
              </tr>
              <tr>
                <td>2X 18-20</td>
                <td>34" - 36"</td>
                <td>30A, 32A, 32B</td>
              </tr>
              <tr>
                <td>3X 20-22</td>
                <td>34" - 36"</td>
                <td>30A, 32A, 32B</td>
              </tr>
              <tr>
                <td>4X 22-24</td>
                <td>34" - 36"</td>
                <td>30A, 32A, 32B</td>
              </tr>
            </tbody>
          </Table>
        </TableWrapper>
      )}
      {activeSizeChartTab === 'sports' && (
        <TableWrapper>
          <Table striped compact>
            <thead>
              <tr>
                <SportsColumn>
                  <FormattedMessage id="fit_guide.size" defaultMessage="Size" />
                </SportsColumn>
                <SportsColumn>
                  <FormattedMessage id="fit_guide.bust" defaultMessage="Bust" />
                </SportsColumn>
                <SportsColumnDecimal>
                  <FormattedMessage
                    id="fit_guide.waist"
                    defaultMessage="Waist"
                  />
                </SportsColumnDecimal>
                <SportsColumnDecimal>
                  <FormattedMessage id="fit_guide.hip" defaultMessage="Hip" />
                </SportsColumnDecimal>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>XS 0-2</td>
                <td>32" - 34"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
              <tr>
                <td>S 4-6</td>
                <td>34" - 36"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
              <tr>
                <td>M 8-10</td>
                <td>34" - 36"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
              <tr>
                <td>L 12-14</td>
                <td>34" - 36"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
              <tr>
                <td>XL 14-16</td>
                <td>34" - 36"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
              <tr>
                <td>1X 16-18</td>
                <td>34" - 36"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
              <tr>
                <td>2X 18-20</td>
                <td>34" - 36"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
              <tr>
                <td>3X 20-22</td>
                <td>34" - 36"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
              <tr>
                <td>4X 22-24</td>
                <td>34" - 36"</td>
                <td>24.5" - 25.5"</td>
                <td>35.5" - 36.5"</td>
              </tr>
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </>
  );
};

export default SizeChartContent;
